import React, { useCallback, useEffect, useState } from 'react';
import { Page } from '../../components/Page';

import {
  Panel,
  PanelHeader,
  PanelLabel,
  PanelFooter,
} from '../../components/Panel';

import {
  Form,
  Button,
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  Row,
  Column,
  TextInput,
  Modal,
  InlineNotification,
} from '@carbon/react';

import { Checkmark, Edit, EditOff, TrashCan } from '@carbon/react/icons';

import './_settings.scss';
import { useDispatch } from 'react-redux';
import {
  useGetTagsQuery,
  useRenameTagMutation,
  useDeleteTagMutation,
  useMergeTagsMutation,
} from '@/redux/new_services/tags';
import { useConfirmRemoveSomething } from '@/lib/modal';
import { showToast } from '@/redux/new_services/toasts';

// Admin tag manager — rename and delete media tags. Renaming onto an
// existing label surfaces a merge prompt; the merge folds the two tags
// into one across every media reference.
export const Tags = () => {
  const dispatch = useDispatch();

  const { data: tags = [], isLoading, isFetching } = useGetTagsQuery();
  const [renameTag, { isLoading: isRenaming }] = useRenameTagMutation();
  const [deleteTag, { isLoading: isDeleting }] = useDeleteTagMutation();
  const [mergeTags, { isLoading: isMerging }] = useMergeTagsMutation();

  const confirmRemoveTag = useConfirmRemoveSomething(
    'Tag entfernen',
    'Möchten Sie den Tag wirklich entfernen? Er wird von allen Elementen abgehängt.'
  );

  // Filtered local list — exact matches by typed search.
  const [search, setSearch] = useState('');

  // Modal state for "rename collided with existing tag — merge?" prompt.
  // We hold the renaming attempt's payload so the confirm-button can replay it
  // with `force: true` once the admin agrees.
  const [mergePrompt, setMergePrompt] = useState(null);

  const handleRename = useCallback(
    async (tagId, newLabel) => {
      try {
        await renameTag({ id: tagId, label: newLabel }).unwrap();
        dispatch(
          showToast({
            kind: 'success',
            title: 'Tag umbenannt',
            caption: `„${newLabel}" wurde gespeichert.`,
          })
        );
        return true;
      } catch (e) {
        if (e?.status === 409 && e?.data?.conflict) {
          setMergePrompt({
            tagId,
            newLabel,
            existingTagId: e.data.existing_tag_id,
          });
          return false;
        }
        dispatch(
          showToast({
            kind: 'error',
            title: 'Umbenennen fehlgeschlagen',
            caption: e?.data?.message || 'Tag konnte nicht umbenannt werden.',
          })
        );
        return false;
      }
    },
    [renameTag, dispatch]
  );

  const handleConfirmMerge = useCallback(async () => {
    if (!mergePrompt) return;
    try {
      await renameTag({
        id: mergePrompt.tagId,
        label: mergePrompt.newLabel,
        force: true,
      }).unwrap();
      dispatch(
        showToast({
          kind: 'success',
          title: 'Tags zusammengeführt',
          caption: `Tags wurden auf „${mergePrompt.newLabel}" vereinigt.`,
        })
      );
      setMergePrompt(null);
    } catch (e) {
      dispatch(
        showToast({
          kind: 'error',
          title: 'Zusammenführen fehlgeschlagen',
          caption: e?.data?.message || 'Tags konnten nicht zusammengeführt werden.',
        })
      );
    }
  }, [mergePrompt, renameTag, dispatch]);

  const handleDelete = useCallback(
    (tag) => {
      confirmRemoveTag(() => {
        deleteTag(tag.details.ID)
          .unwrap()
          .then(() => {
            dispatch(
              showToast({
                kind: 'success',
                title: 'Tag gelöscht',
                caption: `„${tag.label}" wurde entfernt.`,
              })
            );
          })
          .catch((e) => {
            dispatch(
              showToast({
                kind: 'error',
                title: 'Löschen fehlgeschlagen',
                caption: e?.data?.message || 'Tag konnte nicht gelöscht werden.',
              })
            );
          });
      });
    },
    [confirmRemoveTag, deleteTag, dispatch]
  );

  const visibleTags = (tags || []).filter((t) =>
    !search ? true : (t.label || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Page
      pageTitle="Tag-Verwaltung"
      hasSideNav
      isLoading={isLoading || isFetching || isRenaming || isDeleting || isMerging}
    >
      <Row>
        <Column md={5}>
          <Row className="small_row">
            <Column md={4}>
              <Panel small>
                <PanelHeader header>Info</PanelHeader>
                <PanelLabel>
                  Tags werden beim Anlegen oder Bearbeiten von Elementen
                  automatisch gepflegt. Hier können bestehende Tags
                  nachträglich umbenannt oder entfernt werden.
                </PanelLabel>
                <PanelFooter></PanelFooter>
              </Panel>
            </Column>
            <Column md={4}>
              <Panel small>
                <PanelHeader header>Hinweis</PanelHeader>
                <PanelLabel>
                  Wird ein Tag auf einen bereits existierenden Namen
                  umbenannt, fragt das System nach und führt beide Tags
                  zusammen — alle Element-Zuordnungen bleiben erhalten.
                </PanelLabel>
                <PanelFooter></PanelFooter>
              </Panel>
            </Column>
          </Row>
        </Column>
      </Row>
      <Row>
        <Column md={8}>
          <Form>
            <TextInput
              id="tag_admin_search"
              labelText="Suche"
              placeholder="Tag-Name eingeben…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <br />
            <Table size="sm">
              <TableHead>
                <TableRow>
                  <TableHeader>Tag</TableHeader>
                  <TableHeader>Elemente</TableHeader>
                  <TableHeader />
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleTags.map((tag) => (
                  <TagRow
                    key={`tag_row_${tag.details?.ID}`}
                    tag={tag}
                    onRename={handleRename}
                    onDelete={handleDelete}
                  />
                ))}
                {visibleTags.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3}>
                      Keine Tags gefunden.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Form>
        </Column>
      </Row>

      <Modal
        open={Boolean(mergePrompt)}
        onRequestClose={() => setMergePrompt(null)}
        onRequestSubmit={handleConfirmMerge}
        modalHeading="Tags zusammenführen?"
        primaryButtonText="Zusammenführen"
        secondaryButtonText="Abbrechen"
        danger
      >
        {mergePrompt && (
          <>
            <p>
              Ein Tag mit dem Namen <strong>„{mergePrompt.newLabel}"</strong>{' '}
              existiert bereits. Sollen beide Tags zu einem zusammengeführt
              werden?
            </p>
            <InlineNotification
              kind="info"
              hideCloseButton
              lowContrast
              title="Was passiert"
              subtitle="Alle Elemente, die einen der beiden Tags tragen, werden anschließend dem zusammengeführten Tag zugeordnet."
            />
          </>
        )}
      </Modal>
    </Page>
  );
};

// TagRow renders one editable row. We mirror the inline-edit pattern from
// Groups/Types: read-only by default, switch to a text field on Edit, send
// the rename on confirm. The component keeps its own draft state so a fresh
// list fetch doesn't blow away an in-progress edit.
const TagRow = ({ tag, onRename, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(tag.label || '');

  useEffect(() => {
    if (!editing) {
      setDraft(tag.label || '');
    }
  }, [tag.label, editing]);

  const startEdit = () => {
    setDraft(tag.label || '');
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraft(tag.label || '');
  };

  const submitEdit = async () => {
    const trimmed = (draft || '').trim();
    if (!trimmed || trimmed === tag.label) {
      setEditing(false);
      return;
    }
    const ok = await onRename(tag.details.ID, trimmed);
    if (ok) {
      setEditing(false);
    }
  };

  return (
    <TableRow>
      <TableCell>
        {editing ? (
          <TextInput
            id={`tag_edit_${tag.details?.ID}`}
            labelText=""
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                submitEdit();
              } else if (e.key === 'Escape') {
                cancelEdit();
              }
            }}
            autoFocus
          />
        ) : (
          tag.label
        )}
      </TableCell>
      <TableCell>{tag.count ?? 0}</TableCell>
      <TableCell>
        {editing ? (
          <>
            <Button
              renderIcon={(props) => <Checkmark size={20} {...props} />}
              iconDescription="Speichern"
              hasIconOnly
              size="sm"
              kind="primary"
              onClick={submitEdit}
              className="row_editing_button"
            />
            <Button
              renderIcon={(props) => <EditOff size={20} {...props} />}
              iconDescription="Abbrechen"
              hasIconOnly
              size="sm"
              kind="ghost"
              onClick={cancelEdit}
              className="row_editing_button"
            />
          </>
        ) : (
          <>
            <Button
              renderIcon={(props) => <Edit size={20} {...props} />}
              iconDescription="Umbenennen"
              hasIconOnly
              size="sm"
              kind="primary"
              onClick={startEdit}
              className="row_editing_button"
            />
            <Button
              renderIcon={(props) => <TrashCan size={20} {...props} />}
              iconDescription="Entfernen"
              hasIconOnly
              size="sm"
              kind="danger"
              onClick={() => onDelete(tag)}
              className="row_editing_button"
            />
          </>
        )}
      </TableCell>
    </TableRow>
  );
};

export default Tags;
